import { BellRing, Inbox, CalendarClock, ServerCrash, Users } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { useInternalHealth } from '@/hooks/useInternalPanel';
import { InternalTable, EmptyState, LoaderBlock, StatCard, StatusPill, fmtNum, fmtDateTime } from './shared';
import { useTranslation } from 'react-i18next';

export function HealthTab() {
  const { t } = useTranslation();
  const { data, isLoading } = useInternalHealth();

  if (isLoading) return <LoaderBlock />;
  if (!data) return <EmptyState title={t('internal.healthtab.sin_datos_de_salud')} />;

  const firing = data.alarms.filter((a) => a.state === 'ALARM');
  const dlqWithMessages = data.queues.filter((q) => q.isDlq && q.visible > 0);
  const disabledCrons = data.crons.filter((c) => c.state !== 'ENABLED');
  const lambdasConErrores = data.lambdaErrors.filter((l) => l.errors24h > 0);
  const tm = data.talentMap;

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={BellRing}
          label={t('internal.healthtab.alarmas_activas')}
          value={fmtNum(firing.length)}
          sub={t('internal.healthtab.de_n_configuradas', { n: data.alarms.length })}
          tone={firing.length > 0 ? 'rose' : 'green'}
        />
        <StatCard
          icon={Inbox}
          label={t('internal.healthtab.mensajes_en_dlqs')}
          value={fmtNum(dlqWithMessages.reduce((a, q) => a + q.visible, 0))}
          sub={t('internal.healthtab.n_dlqs_monitoreadas', { n: data.queues.filter((q) => q.isDlq).length })}
          tone={dlqWithMessages.length > 0 ? 'rose' : 'green'}
        />
        <StatCard
          icon={CalendarClock}
          label="Crons"
          value={`${fmtNum(data.crons.length - disabledCrons.length)}/${fmtNum(data.crons.length)}`}
          sub={t('internal.healthtab.habilitados')}
          tone={disabledCrons.length > 0 ? 'amber' : 'green'}
        />
        <StatCard
          icon={ServerCrash}
          label={t('internal.healthtab.lambdas_con_errores_24h')}
          value={fmtNum(lambdasConErrores.length)}
          tone={lambdasConErrores.length > 0 ? 'amber' : 'green'}
        />
        {tm && (
          <StatCard
            icon={Users}
            label={t('internal.healthtab.mapa_universo')}
            value={`${fmtNum(tm.activos)} / ${fmtNum(tm.conRecord + tm.sinRecord)}`}
            sub={t('internal.healthtab.mapa_universo_sub', { sinRecord: tm.sinRecord, onboarding: tm.onboarding })}
            tone={tm.ok ? 'green' : 'amber'}
          />
        )}
      </div>

      {/* Integridad del Mapa de Talento (08/09): el community map es la verdad;
          la vista org tiene que tener EXACTAMENTE una fila por activo. */}
      {tm && (
        <Card className="p-5">
          <h3 className="text-sm font-semibold mb-3">{t('internal.healthtab.mapa_integridad')}</h3>
          <ul className="text-sm space-y-1.5">
            <li className="flex items-center justify-between gap-3">
              <span>{t('internal.healthtab.mapa_corte')}</span>
              {/* Sol 24/09: onboarding = 3 meses rodantes desde el ingreso; un corte fijo es anomalía. */}
              <StatusPill
                level={tm.cutoffOverride ? 'warn' : 'ok'}
                label={tm.cutoffOverride
                  ? t('internal.healthtab.mapa_corte_fijo', { date: tm.cutoffOverride })
                  : t('internal.healthtab.mapa_corte_rodante', { date: tm.limiteEfectivo })}
              />
            </li>
            {([
              ['sinLider', tm.issues.sinLider, tm.issues.sinLider.length > 3],
              ['emailsSucios', tm.issues.emailsSucios, tm.issues.emailsSucios.length > 0],
              ['duplicados', tm.issues.duplicados, tm.issues.duplicados.length > 0],
              ['recordsDeInactivos', tm.issues.recordsDeInactivos, tm.issues.recordsDeInactivos.length > 0],
            ] as [string, string[], boolean][]).map(([key, list, bad]) => (
              <li key={key} className="flex items-start justify-between gap-3">
                <span>{t(`internal.healthtab.mapa_issue_${key}`)}</span>
                <span className="text-right">
                  <StatusPill level={bad ? 'bad' : 'ok'} label={fmtNum(list.length)} />
                  {list.length > 0 && (
                    <span className="block font-mono text-[11px] text-muted-foreground max-w-[420px] truncate" title={list.join(', ')}>
                      {list.slice(0, 4).join(', ')}{list.length > 4 ? '…' : ''}
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {data.partialFailures.length > 0 && (
        <p className="text-xs text-amber-600 dark:text-amber-400">
          {t('internal.healthtab.no_se_pudo_consultar')} {data.partialFailures.join(', ')} {t('internal.healthtab.el_resto_del_panel_es_valido')}
        </p>
      )}

      <Card className="p-5">
        <h3 className="text-sm font-semibold mb-3">{t('internal.healthtab.alarmas_de_cloudwatch')}</h3>
        {data.alarms.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">{t('internal.healthtab.sin_alarmas_configuradas')}</p>
        ) : (
          <div className="grid gap-2 md:grid-cols-2">
            {data.alarms.map((a) => (
              <div key={a.name} className="flex items-start justify-between gap-2 border rounded-lg p-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{a.name}</div>
                  {a.description && (
                    <div className="text-xs text-muted-foreground truncate">{a.description}</div>
                  )}
                  {a.state === 'ALARM' && a.reason && (
                    <div className="text-xs text-rose-600 dark:text-rose-400 mt-1">{a.reason}</div>
                  )}
                  {a.updatedAt && (
                    <div className="text-[11px] text-muted-foreground mt-0.5">desde {fmtDateTime(a.updatedAt)}</div>
                  )}
                </div>
                <StatusPill
                  level={a.state === 'ALARM' ? 'bad' : a.state === 'OK' ? 'ok' : 'warn'}
                  label={a.state === 'INSUFFICIENT_DATA' ? t('internal.healthtab.sin_datos') : a.state}
                />
              </div>
            ))}
          </div>
        )}
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-5">
          <h3 className="text-sm font-semibold mb-3">{t('internal.healthtab.colas_sqs')}</h3>
          {data.queues.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">{t('internal.healthtab.sin_colas')}</p>
          ) : (
            <InternalTable
              rows={data.queues}
              rowKey={(q) => q.name}
              columns={[
                { key: 'name', label: t('internal.healthtab.cola'), className: 'font-mono text-xs truncate max-w-[220px]', render: (q) => q.name },
                { key: 'visible', label: t('internal.healthtab.en_cola'), align: 'right', render: (q) => fmtNum(q.visible) },
                { key: 'inFlight', label: t('internal.healthtab.en_vuelo'), align: 'right', render: (q) => fmtNum(q.inFlight) },
                {
                  key: 'state', label: t('internal.healthtab.estado'), align: 'right',
                  render: (q) => (
                    <StatusPill
                      level={q.isDlq && q.visible > 0 ? 'bad' : 'ok'}
                      label={q.isDlq ? (q.visible > 0 ? t('internal.healthtab.dlq_con_mensajes') : t('internal.healthtab.dlq_vacia')) : 'OK'}
                    />
                  ),
                },
              ]}
            />
          )}
        </Card>
        <Card className="p-5">
          <h3 className="text-sm font-semibold mb-3">{t('internal.healthtab.crons_eventbridge')}</h3>
          {data.crons.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">{t('internal.healthtab.sin_crons')}</p>
          ) : (
            <InternalTable
              rows={data.crons}
              rowKey={(c) => c.name}
              columns={[
                { key: 'name', label: t('internal.healthtab.cron'), className: 'text-xs truncate max-w-[200px]', render: (c) => c.name },
                { key: 'schedule', label: t('internal.healthtab.schedule'), className: 'font-mono text-[11px] text-muted-foreground', render: (c) => c.schedule },
                { key: 'state', label: t('internal.healthtab.estado'), align: 'right', render: (c) => <StatusPill level={c.state === 'ENABLED' ? 'ok' : 'warn'} label={c.state} /> },
              ]}
            />
          )}
        </Card>
      </div>

      <Card className="p-5">
        <h3 className="text-sm font-semibold mb-3">{t('internal.healthtab.lambdas_ultimas_24_h')}</h3>
        {data.lambdaErrors.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">{t('internal.healthtab.sin_actividad_registrada')}</p>
        ) : (
          <>
            <InternalTable
              rows={data.lambdaErrors.slice(0, 15)}
              rowKey={(l) => l.functionName}
              columns={[
                { key: 'fn', label: t('internal.healthtab.funcion'), className: 'font-mono text-xs truncate max-w-[300px]', render: (l) => l.functionName },
                {
                  key: 'errors', label: t('internal.healthtab.errores'), align: 'right',
                  render: (l) => <span className={l.errors24h > 0 ? 'text-rose-600 dark:text-rose-400 font-semibold' : ''}>{fmtNum(l.errors24h)}</span>,
                },
                { key: 'inv', label: t('internal.healthtab.invocaciones'), align: 'right', render: (l) => fmtNum(l.invocations24h) },
              ]}
            />
            <p className="text-[11px] text-muted-foreground mt-2">
              mostrando {Math.min(15, data.lambdaErrors.length)} de {data.lambdaErrors.length} {t('internal.healthtab.funciones_con_actividad')}
            </p>
          </>
        )}
      </Card>
    </div>
  );
}
